import { Component, Output, EventEmitter, Input, inject } from '@angular/core';
import { ModalFormContainerComponent } from '../../../../ui/modal-form-container/modal-form-container.component';
import { MatIcon } from '@angular/material/icon';
import { InputFieldComponent } from '../../../../ui/inputField/inputField.component';
import { GroupSelectionInputComponent } from '../../../../inputs/group-selection-input/group-selection-input.component';
import { CustomizableButtonComponent } from '../../../../ui/customizable-button/customizable-button.component';
import { AssignOrganizationsTableComponent } from '../../../../tables/assign-organizations-modal/assign-organizations-modal.component';
import { AssignTagsModalComponent } from '../../../../tables/assign-tags-modal/assign-tags-modal.component';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import {
  IContact,
  IOrganization,
  ITag,
} from '../../../../../types/contactModule';
import { IContactFormDataKeys } from '../../../../forms/create-contact-form/create-contact-form.component';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { countries } from '../../../../../../utils/countriesList';
import { getCountryCode } from '../../../../../../utils/utils';
import { CsvServiceService } from '../../../../../services/csv/csv-service.service';

@Component({
  selector: 'edit-uploaded-contact',
  standalone: true,
  imports: [
    ModalFormContainerComponent,
    ReactiveFormsModule,
    InputFieldComponent,
    GroupSelectionInputComponent,
    CustomizableButtonComponent,
    AssignOrganizationsTableComponent,
    AssignTagsModalComponent,
    NgxMatInputTelComponent,
  ],
  templateUrl: './edit-uploaded-contact.component.html',
  styleUrls: [
    '../../../../forms/create-contact-form/contact-form.css',
    './edit-uploaded-contact.component.css',
  ],
})
export class EditUploadedContactComponent {
  constructor(private builder: FormBuilder) {}
  csvService = inject(CsvServiceService);

  countries = countries;

  selectedOrganizations: IOrganization[] = [];
  selectedTags: ITag[] = [];

  // modal controls
  showAddOrganizationModal: boolean = false;
  showAddTagModal: boolean = false;

  openAddOrganizationModal() {
    this.showAddOrganizationModal = true;
  }
  closeAddOrganizationModal() {
    this.showAddOrganizationModal = false;
  }
  openAddTagModal() {
    this.showAddTagModal = true;
  }
  closeAddTagModal() {
    this.showAddTagModal = false;
  }

  @Input({ required: true }) selectedContact!: IContact | null;
  @Input({ required: true }) index!: string | number;
  @Output() closeModalEvent = new EventEmitter();
  contactForm = this.builder.group({
    firstName: ['', Validators.required],
    // Optional, as everywhere else: many imported rows have no surname.
    lastName: [''],
    primaryContactNumber: ['', Validators.required],
    emailAddress: ['', [Validators.email]],
    gender: [''],
    birthDate: [''],
    country: [''],
    city: [''],
  });

  submitted: boolean = false;

  getFormInput(name: IContactFormDataKeys) {
    return this.contactForm.get(name);
  }

  get emailErrorMsg() {
    const email = this.getFormInput('emailAddress');
    if (email?.errors?.['email']) {
      return 'Enter an email address like name@example.com';
    }
    return '';
  }

  /** The checks this row failed, so the form can say what to fix. */
  @Input() issues: { propertyName: string; message: string }[] = [];

  issueFor(field: string): string {
    if (this.submitted) return '';
    return this.issues?.find((issue) => issue.propertyName === field)?.message ?? '';
  }

  // update the organization selection in the parent cmpnt
  updateSelectedOrganizations(selections: IOrganization[]) {
    this.selectedOrganizations = [...(selections ?? [])];
  }

  // update the organization selection in the parent cmpnt
  updateSelectedTags(selections: ITag[]) {
    this.selectedTags = [...(selections ?? [])];
  }

  removeSelectedOrganization(organization: any) {
    const passedOrg = organization as IOrganization;
    this.selectedOrganizations = this.selectedOrganizations.filter(
      (org) => org.id !== organization.id
    );
  }

  removeSelectedTag(tag: any) {
    const passedTag = tag as ITag;
    this.selectedTags = this.selectedTags.filter(
      (tag) => tag.id !== passedTag.id
    );
  }

  handleSubmit() {
    this.submitted = true;
    if (this.contactForm.valid) {
      const values = this.contactForm.value as IContact;
      const previousCountry = (this.selectedContact?.country ?? '').toString().trim();
      const nextCountry = (values.country ?? '').toString().trim();
      let countryCode = this.selectedContact?.countryCode;
      if (nextCountry !== previousCountry) {
        // A new country is also the region national-format numbers are read in.
        try {
          countryCode = nextCountry ? getCountryCode(nextCountry)?.countryCode ?? countryCode : countryCode;
        } catch {
          // Unknown country: keep the region the row already had.
        }
      }
      this.csvService.updateValidatedContact(
        {
          // Keep every field the form does not show (region, stage, type…).
          ...(this.selectedContact ?? {}),
          ...values,
          // "Not set" is stored as OTHER, as a blank gender cell is at column mapping.
          gender: values.gender || 'OTHER',
          countryCode,
          companyEntities: this.selectedOrganizations,
          entityTags: this.selectedTags,
          id: this.selectedContact?.id,
        } as IContact,
        this.index
      );
      this.closeModal();
    }
    // console.log(this.contactForm, 'the form object');
  }

  closeModal() {
    this.closeModalEvent.emit();
  }

  ngOnChanges() {
    this.contactForm.patchValue(
      this.selectedContact as typeof this.contactForm.value
    );
    this.selectedOrganizations = [...((this.selectedContact?.companyEntities ?? []) as IOrganization[])];
    this.selectedTags = [...((this.selectedContact?.entityTags ?? []) as ITag[])];
  }
}
